import { cn } from '@/utils';
import { WarningIcon } from '@phosphor-icons/react';
import type { PropsWithChildren } from 'react';
import { PodcastRssFeed } from './PodcastRssFeed';
import type { RSSFeedElementProps } from './RSSFeed.types';
import { YouTubeRssFeed } from './YouTubeRssFeed';
import { getSource } from './util';

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

export function RSSFeedElement(props: PropsWithChildren<RSSFeedElementProps>) {
  const { element, attributes, onClick, children, className } = props;
  const { url, data, columns, gap, padding, showCount, isError, isLoading } = element.attrs;

  if (!url) {
    return (
      <div className={cn('grid relative', className)} {...attributes}>
        {children}
      </div>
    );
  }

  if (isLoading || isError || !data) {
    return (
      <div
        className={cn('grid relative w-full h-fit', COLUMNS_MAP[columns || '3'])}
        {...attributes}
        style={{ gap: gap || '16px', padding: padding }}
      >
        {Array.from({ length: showCount || 3 }).map((_, index) => (
          <div key={`index-${index}`} className="flex flex-col gap-4 items-start">
            <div
              className={cn('relative w-full rounded-lg overflow-hidden bg-gray-200', !isError && 'animate-pulse')}
              style={{
                paddingBottom: '56%',
              }}
            >
              <div className={cn('w-full h-full top-0 left-0 absolute flex items-center justify-center ')}>
                {isError && <WarningIcon size={24} className="text-gray-400" />}
              </div>
            </div>
            <div className={cn('w-full h-4 bg-gray-200 rounded-full', !isError && 'animate-pulse')} />
            <div className={cn('w-full h-4 bg-gray-200 rounded-full', !isError && 'animate-pulse')} />
          </div>
        ))}
        {children}
      </div>
    );
  }

  const source = getSource(url);
  if (source === 'youtube') {
    return (
      <YouTubeRssFeed element={element} attributes={attributes} onClick={onClick}>
        {children}
      </YouTubeRssFeed>
    );
  }
  if (source === 'podcast') {
    return (
      <PodcastRssFeed element={element} attributes={attributes} onClick={onClick}>
        {children}
      </PodcastRssFeed>
    );
  }

  return (
    <div className={cn('relative flex flex-col gap-4')} {...attributes}>
      <p className="text-sm text-gray-900 text-left leading-tight">Unsupported RSS feed source</p>
      <p className="text-xs text-gray-500 text-left leading-tight">
        Only YouTube RSS & Podcast RSS feeds are supported at the moment.
      </p>
    </div>
  );
}
